import React, { useState, Fragment, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TopActions from '../actions/TopActions';
import Toast from '../actions/Toasts';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import apiService, { authFetch } from '../../services/apiServices';
import InputController from '../../controllers/InputController';
import SelectController from '../../controllers/SelectController';
import { workTypeOptions, findWorkType } from '../../utils/workTypeOptions';
import { departmentsForBranch } from '../../utils/departmentBranches';
import { fetchContractFields, fillHtmlTemplate } from '../../utils/contractFill';
import { useContractPayRates, applyPayChange, fillMissingHourly } from '../../utils/contractPay';
import { downloadContractDocument } from '../../utils/wordTemplate';
import { WordPreview } from '../contracts-templates/WordTemplatePanel';
import dateFormats from '../../services/dateFormats';
import { useParams } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import { Switch, Box, Chip, Avatar, Divider, Stack, alpha, Button, Tooltip, ToggleButton, ToggleButtonGroup, CircularProgress, TextField, Menu, MenuItem } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SaveIcon from '@mui/icons-material/Save';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import ArticleIcon from '@mui/icons-material/Article';
import EditNoteIcon from '@mui/icons-material/EditNote';

// Rreshtat e departamenteve/filialeve vijnë me `Id`, kontrata ruan vetëm
// numrin — krahasimi `e.id === …` s'gjente kurrë asgjë.
const findById = (rows, id) => {
  if (id === undefined || id === null || id === '') return null;
  return (rows || []).find((r) => String(r?.Id ?? r?.id) === String(id)) || null;
};

export default function EditWorkerContract() {
  //const classes = useStyles();
  const { t } = useTranslation();
  const alert = { open: false, message: '', variant: '' };
  const [toast, setToast] = useState(alert);
  const [worker, setWorker] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branch, setBranch] = useState([]);
  const [contractDetails, setContractDetails] = useState([]);
  const [fiscalDefaults, setFiscalDefaults] = useState({ employee: 5, employer: 5, brutoNeto: 1 });
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      WithTax: true,
      DestroyContract: false,
      EmployeeTax: 0,
      EmployerTax: 0,
      TermsOfContract: '',
      ContractType: null,
      WorkType: null,
      SalaryType: null,
      Department: null,
      BranchId: null,
      BrutoNeto: null,
      Template: null,
      StartDate: '',
      EndDate: '',
      BaseSalary: '',
      HourSalary: '',
      HourSalaryWeekend: '',
      HourSalaryHoliday: '',
      DestroyDate: null,
      DestroyReason: '',
    },
  });
  // Keep these option lists stable across renders to avoid re-running effects endlessly
  const salaryTypes = useMemo(
    () => [{ id: 0, name: '' }, { id: 1, name: t('worker.monthly') }, { id: 2, name: t('worker.byHour') }],
    [t]
  );
  const contractExtension = useMemo(
    () => [{ id: 0, name: '' }, { id: 1, name: t('worker.monthly') }, { id: 2, name: t('worker.yearly') }],
    [t]
  );
  const brutoNeto = useMemo(
    () => [{ id: 0, name: '' }, { id: 1, name: t('worker.bruto') }, { id: 2, name: t('worker.neto') }],
    [t]
  );
  let { id } = useParams();
  const [loader, setLoader] = useState(true);
  const [contractTemplate, setContractTemplate] = useState()
  // Vlerat e formatuara nga serveri (data, paga, departamenti me emër).
  const [serverFields, setServerFields] = useState(null);
  // Kontrata e plotësuar nga dokumenti Word i shabllonit — për parapamjen.
  const [filledDocx, setFilledDocx] = useState(null);
  const [docBusy, setDocBusy] = useState(false);
  const isWordTemplate = Boolean(contractTemplate?.HasDocx);
  const payRates = useContractPayRates();
  const [templates, setTemplates] = useState([])
  const [view, setView] = useState('form'); // 'form' | 'preview'
  const [templateLoading, setTemplateLoading] = useState(false);
  const workerTags = ["FirstName", "LastName", "ParentName", "PersonalId", "Address", "Phone", "PostalCode", "BankAccount", "BankName", "Birthday", "Country", "Place", "Email", "Gender", "MaritalStatus", "Qualification", "Profession", "ContractType", "StartDate", "EndDate", "WorkType", "SalaryType", "Department", "BaseSalary", "BrutoNeto", "HourSalary", "HourSalaryWeekend", "HourSalaryHoliday", "Image", "TermsOfContract"]
  const loadWorkTypes = useCallback(async (currentWorkType) => {
    let rows = [];
    try {
      const wt = await apiService().get('worktype');
      if (wt?.success) rows = wt.data;
    } catch (err) {
      console.error('Failed to load work types', err);
    }
    const options = workTypeOptions(rows);
    setWorkTypes(options);
    if (currentWorkType) setValue('WorkType', findWorkType(options, currentWorkType));
  }, [setValue]);

  const loadFiscalDefaults = useCallback(async () => {
    let defaults = { employee: 5, employer: 5, brutoNeto: 1 };
    try {
      const res = await apiService().get('fiscalPolitics');
      const fp = res?.success && Array.isArray(res.data) && res.data.length ? res.data[0] : null;
      if (fp) {
        defaults = {
          employee: Number(fp.EmployeeTax ?? fp.employeeTax ?? 5),
          employer: Number(fp.EmployerTax ?? fp.employerTax ?? 5),
          brutoNeto: fp.Type ?? fp.type ?? 1,
        };
      }
    } catch (err) {
      console.error('Failed to load fiscal defaults', err);
    }
    setFiscalDefaults(defaults);
  }, []);

  const getContractTemplate = useCallback((id) => {
    if (id === undefined || id === null) return;
    setTemplateLoading(true);
    apiService().query('getTemplate', 'id=' + id)
      .then(response => {
        if (response.success && Array.isArray(response.data) && response.data.length) {
          setContractTemplate(response.data[0]);
        } else {
          setContractTemplate(null);
        }
      })
      .finally(() => setTemplateLoading(false));
  }, []);

  const templatesRef = useRef([]);
  const fiscalRef = useRef({ employee: 5, employer: 5, brutoNeto: 1 });
  templatesRef.current = templates;
  fiscalRef.current = fiscalDefaults || fiscalRef.current;

  const getContract = useCallback(() => {
    apiService()
      .query('getContract', 'id=' + id)
      .then((wc) => {
        if (wc.success && Array.isArray(wc.data) && wc.data.length) {
          //setContracts(wc.data)
          const contractRow = wc.data[0];
          getWorker(contractRow.WorkerId)
          contractRow.Birthday = dateFormats(contractRow.Birthday);
          contractRow.StartDate = dateFormats(contractRow.StartDate);
          contractRow.EndDate = dateFormats(contractRow.EndDate);

          const fields = Object.keys(contractRow);
          fields.forEach((field) => {
            if (field === 'SalaryType') {
              setValue(
                'SalaryType', salaryTypes.find(function (e) {
                  return e.id === contractRow.SalaryType;
                }) || null
              );
            }
            else if (field === 'ContractType') {
              setValue(
                'ContractType', contractExtension.find(function (e) {
                  return e.id === contractRow.ContractType;
                }) || null
              );
            }
            else if (field === 'Template') {
              setValue(
                'Template', templatesRef.current?.find(function (e) {
                  return e.id === contractRow.Template;
                }) || null
              );
            }
            else if (field === 'BrutoNeto') {
              setValue(
                'BrutoNeto', brutoNeto?.find(function (e) {
                  return e.id === contractRow.BrutoNeto;
                }) || null
              );
            }
            else if (field === 'EmployeeTax') {
              !contractRow[field] ? setValue(field, 0) : setValue(field, contractRow[field])
              contractRow[field] ? setValue('WithTax', true) : setValue('WithTax', false)
            }
            else if (field === 'EmployerTax') {
              !contractRow[field] ? setValue(field, 0) : setValue(field, contractRow[field])
              contractRow[field] ? setValue('WithTax', true) : setValue('WithTax', false)
            }
            else if (field === 'DestroyDate') {
              !contractRow[field] ? setValue(field, null) : setValue(field, dateFormats(contractRow[field]))
              contractRow[field] ? setValue('DestroyContract', true) : setValue('DestroyContract', false)
            }
            else if (field === 'DestroyReason') {
              !contractRow[field] ? setValue(field, null) : setValue(field, contractRow[field])
              contractRow[field] ? setValue('DestroyContract', true) : setValue('DestroyContract', false)
            }
            // Lloji i punës, departamenti dhe filiali vendosen si opsione më poshtë,
            // kur të vijnë listat — numri i zhveshur e linte fushën bosh me "X".
            else if (field === 'WorkType' || field === 'Department' || field === 'BranchId') {
              // asgjë këtu
            }
            else {
              setValue(field, contractRow[field])
            }
          });
          setContractDetails(contractRow);
          setValue('EmployeeTax', contractRow.EmployeeTax ?? fiscalRef.current.employee ?? 5);
          setValue('EmployerTax', contractRow.EmployerTax ?? fiscalRef.current.employer ?? 5);
          setValue('WithTax', contractRow.WithTax ?? true);
          if (!watch('BrutoNeto')) {
            const brutoDefault = brutoNeto.find((o) => String(o.id) === String(contractRow.BrutoNeto || fiscalRef.current.brutoNeto)) || brutoNeto[0];
            setValue('BrutoNeto', brutoDefault);
          }

          loadWorkTypes(wc.data[0].WorkType);

          apiService()
            .get('department')
            .then((dep) => {
              if (dep.success) {
                const deps = (dep.data || []).filter((d) => d.Active !== false);
                setDepartments(deps);
                setValue('Department', findById(dep.data, wc.data[0].Department));
              } else {
                console.error('Error:' + dep.errorMessage);
              }
            });
          apiService()
            .get('branch')
            .then((br) => {
              if (br.success) {
                setBranch((br.data || []).filter((b) => b.Active !== false));
                setValue('BranchId', findById(br.data, contractRow.BranchId));
              } else {
                console.error('Error:' + br.errorMessage);
              }
            });
          setLoader(false)
        } else {
          setLoader(false);
          setToast({
            open: true,
            message: 'Kontrata nuk u gjet.',
            variant: 'error',
          });
        }
      });
  // Pa `templates`/`fiscalDefaults` te varësitë: secila i jepte getContract një
  // identitet të ri, efekti e thërriste sërish, ai rimbushte formën — cikël pa
  // fund, dhe "Lloji i punës" shfaqej e zhdukej. Vlerat e tyre lexohen nga ref-et.
  }, [brutoNeto, contractExtension, id, loadWorkTypes, salaryTypes, setValue]);
  const getWorker = (id) => {
    apiService()
      .query('getWorker', 'id=' + id)
      .then((wc) => {
        if (wc.success && Array.isArray(wc.data) && wc.data.length) {
          setWorker(wc.data[0])
        } else {
          console.log('Error:' + wc.errorMessage);
        }
      });
  }

  const toLowerKeys = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      acc[camelKey] = obj[key];
      return acc;
    }, {});
  }

  const getTemplates = useCallback(() => {
    apiService().get('contractsTemplates')
      .then(response => {
        if (response.success) {
          let arr = []
          let data = response.data
          data?.forEach(d => {
            //let obj = new ModelPurchaseDetails(d)
            arr.push(toLowerKeys(d))
          })
          setTemplates(arr)
        }
      })
  }, []);

  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  // Listat ngarkohen një herë; kontrata një herë për çdo id.
  useEffect(() => {
    getTemplates();
    loadFiscalDefaults();
  }, [getTemplates, loadFiscalDefaults]);
  useEffect(() => {
    getContract();
  }, [getContract]);

  useEffect(() => {
    if (contractDetails?.Template !== undefined && contractDetails?.Template !== null) {
      getContractTemplate(contractDetails.Template);
    }
    if (templates?.length) {
      setValue(
        'Template', templates.find(function (e) {
          return e.id === contractDetails.Template;
        }) || null
      );
    }
  }, [contractDetails, templates, getContractTemplate, setValue])

  // Reload template preview whenever the Template field changes in the form
  const watchedTemplate = watch('Template');
  useEffect(() => {
    const templateId = watchedTemplate?.id ?? watchedTemplate?.Id ?? watchedTemplate;
    if (templateId) {
      getContractTemplate(templateId);
    } else {
      setContractTemplate(null);
    }
  }, [watchedTemplate, getContractTemplate]);

  // const onSubmit = (data) => {
  //   console.log(data)
  // }

  const extractId = (val, fallback) => {
    if (val === undefined || val === null) return fallback ?? null;
    if (typeof val === 'object') {
      return val.id ?? val.Id ?? val.value ?? fallback ?? null;
    }
    return val;
  };

  const onSubmit = (data) => {
    const payload = {
      ...data,
      ContractType: extractId(data.ContractType, contractDetails?.ContractType),
      WorkType: extractId(data.WorkType, contractDetails?.WorkType),
      SalaryType: extractId(data.SalaryType, contractDetails?.SalaryType),
      Department: extractId(data.Department, contractDetails?.Department),
      BranchId: extractId(data.BranchId, contractDetails?.BranchId),
      BrutoNeto: extractId(data.BrutoNeto, contractDetails?.BrutoNeto),
      Template: extractId(data.Template, contractDetails?.Template),
      CreatedBy: '',
    };

    apiService()
      .put('workerContract', payload)
      .then((response) => {
        if (response.success) {
          setToast({
            open: true,
            message: 'U ruajten me sukses!',
            variant: 'success',
          });
          getContract();
        } else {
          setToast({
            open: true,
            message: 'Ka ndodhur nje gabim!',
            variant: 'error',
          });
        }
      });
  };

  // Kontratë e ruajtur me vetëm pagë bazë: fushat e orës plotësohen sipas ligjit.
  useEffect(() => {
    if (contractDetails) fillMissingHourly(getValues, setValue, payRates);
  }, [contractDetails, payRates, getValues, setValue]);

  useEffect(() => {
    let alive = true;
    fetchContractFields(id).then((f) => { if (alive) setServerFields(f); });
    return () => { alive = false; };
  }, [id, contractDetails]);

  // Parapamja e kontratës nga Word: dokumenti i plotësuar nga serveri, me dizajnin e vet.
  useEffect(() => {
    if (!isWordTemplate || view !== 'preview') return undefined;
    let alive = true;
    (async () => {
      const tplId = contractTemplate?.Id ?? contractTemplate?.id;
      const res = await authFetch(`/api/worker-contracts/${id}/document?format=docx&templateId=${tplId}`).catch(() => null);
      if (alive && res?.ok) setFilledDocx(await res.blob());
    })();
    return () => { alive = false; };
  }, [isWordTemplate, view, id, contractTemplate, contractDetails]);

  const [downloadAnchor, setDownloadAnchor] = useState(null);
  const [protocolBusy, setProtocolBusy] = useState(false);
  const assignProtocol = async () => {
    setProtocolBusy(true);
    try {
      const res = await authFetch(`/api/worker-contracts/${id}/protocol`, { method: 'POST' });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.data?.ProtocolNumber) {
        setValue('ProtocolNumber', j.data.ProtocolNumber, { shouldDirty: true });
        setToast({ open: true, message: `Numri i protokollit: ${j.data.ProtocolNumber}`, variant: 'success' });
      } else {
        setToast({ open: true, message: j.error || 'Numri nuk u caktua.', variant: 'error' });
      }
    } finally {
      setProtocolBusy(false);
    }
  };
  const downloadWord = async (format, opts) => {
    setDownloadAnchor(null);
    setDocBusy(true);
    try {
      await downloadContractDocument(id, format, contractTemplate?.Id ?? contractTemplate?.id, opts);
      // Gjenerimi i parë cakton numrin e protokollit te serveri — forma duhet ta
      // dijë, që një «Ruaj» pas tij të mos e fshijë.
      if (!getValues('ProtocolNumber')) {
        const f = await fetchContractFields(id);
        if (f?.ContractNumber) setValue('ProtocolNumber', f.ContractNumber);
      }
    } catch (e) {
      setToast({ open: true, message: e.message, variant: 'error' });
    } finally {
      setDocBusy(false);
    }
  };

  const replaceText = () => {
    const html = contractTemplate?.Content || contractTemplate?.Text || '';
    if (serverFields) return fillHtmlTemplate(html, serverFields);
    let text = html;
    const data = { ...(worker || {}), ...(contractDetails || {}) };

    (workerTags || []).forEach((tag) => {
      const value = data[tag];
      if (tag === 'Image') {
        const imgHtml = value ? `<img src="${value}" alt="contract" />` : '--';
        text = text.replaceAll(`@${tag}`, imgHtml);
      } else {
        text = text.replaceAll(`@${tag}`, value || '--');
      }
    });

    text = text.replaceAll('<!-- pagebreak -->', '<br style="page-break-before: always">');
    return text;
  }

  const wordDoc = (element, filename = '') => {
    const content = replaceText();
    if (!content || content.trim() === '') {
      setToast({ open: true, message: 'Nuk ka template të zgjedhur për shkarkim.', variant: 'warning' });
      return;
    }
    const header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Kontratë</title></head><body>";
    const footer = "</body></html>";
    const html = header + content + footer;
    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const url = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(html);
    filename = filename ? filename + '.doc' : 'kontrate.doc';
    const downloadLink = document.createElement('a');
    document.body.appendChild(downloadLink);
    if (navigator.msSaveOrOpenBlob) {
      navigator.msSaveOrOpenBlob(blob, filename);
    } else {
      downloadLink.href = url;
      downloadLink.download = filename;
      downloadLink.click();
    }
    document.body.removeChild(downloadLink);
  }

  const printContract = () => {
    const content = replaceText();
    if (!content || content.trim() === '') {
      setToast({ open: true, message: 'Nuk ka template të zgjedhur për printim.', variant: 'warning' });
      return;
    }
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><meta charset='utf-8'><title>Kontratë - ${worker?.FirstName ?? ''} ${worker?.LastName ?? ''}</title>
      <style>
        body { font-family: 'Times New Roman', serif; margin: 2cm 2.5cm; font-size: 12pt; line-height: 1.6; color: #111; }
        h1,h2,h3 { margin-bottom: 0.4em; }
        p { margin-bottom: 0.5em; }
        @media print { body { margin: 1.5cm 2cm; } }
      </style></head><body>${content}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  const setTaxes = (isTrue, property) => {
    setValue(property, isTrue);
    if (property === "WithTax") {
      if (!isTrue) {
        setValue('EmployeeTax', 0);
        setValue('EmployerTax', 0);
      }
      else {
        const defaults = fiscalDefaults || {};
        !watch('EmployeeTax') ? setValue('EmployeeTax', defaults.employee ?? 5) : setValue('EmployeeTax', watch('EmployeeTax'));
        !watch('EmployerTax') ? setValue('EmployerTax', defaults.employer ?? 5) : setValue('EmployerTax', watch('EmployerTax'));
      }
    }
    else if(property === "DestroyContract") {
      if (!isTrue) {
        setValue('DestroyDate', null);
        setValue('DestroyReason', null);
      }
      else {
        !watch('DestroyDate') ? setValue('DestroyDate', dateFormats(new Date())) : setValue('DestroyDate', watch('DestroyDate'));
        !watch('DestroyReason') ? setValue('DestroyReason', " ") : setValue('DestroyReason', watch('DestroyReason'));
      }
    }
  }

  return (
    <Box sx={{ display:'flex', flexDirection:'column', minHeight:'100%', bgcolor: t=>t.palette.mode==='dark'?'#0b0f19':'#f8fafc' }}>
      <Toast {...toast} />
      <input type="file" id="FileUpload1" style={{ display: 'none' }} />
      <div id="toWordDoc" style={{ display: 'none' }}>
        <p>Emri: {worker?.FirstName + " " + worker?.LastName}</p>
        <p>Kohezgjatja: {watch('StartDate') + " - " + watch('EndDate')}</p>
        <p>{t('worker.baseSalary')}: {contractDetails?.BaseSalary}</p>
        <p>{t('worker.hourlyRate')}: {contractDetails?.HourSalary}</p>
      </div>

      <Box sx={{px:{xs:2,lg:4}, pt:2, pb:3, flex:1, display:'flex', flexDirection:'column', gap:2}}>

        {/* ── Page Header ── */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{
            width:44, height:44, borderRadius:2, flexShrink:0,
            background:'linear-gradient(135deg,#3E8FA3,#1B6A7A)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <DescriptionOutlinedIcon sx={{color:'#fff', fontSize:22}}/>
          </Box>
          <Box sx={{flex:1, minWidth:0}}>
            <Typography variant="h6" sx={{fontWeight:800, letterSpacing:'-0.01em', lineHeight:1.15, fontSize:'1.1rem'}}>
              {t('worker.editContract')}
            </Typography>
            <Typography variant="caption" sx={{color:'text.secondary', fontSize:'0.74rem'}}>
              {(() => {
                const no = watch('ProtocolNumber') ? `Nr. ${watch('ProtocolNumber')}` : `Kontrata #${id}`;
                return worker?.FirstName ? `${worker.FirstName} ${worker.LastName} · ${no}` : no;
              })()}
            </Typography>
          </Box>
          {/* View toggle */}
          <ToggleButtonGroup
            value={view} exclusive
            onChange={(_, v) => { if (v) setView(v); }}
            size="small"
            sx={{ height:34, '& .MuiToggleButton-root': { textTransform:'none', fontWeight:700, fontSize:'0.78rem', px:1.5, borderColor:'#e2e8f0' } }}
          >
            <ToggleButton value="form">
              <EditNoteIcon sx={{fontSize:15, mr:0.6}}/> Forma
            </ToggleButton>
            <ToggleButton value="preview">
              <ArticleIcon sx={{fontSize:15, mr:0.6}}/> Preview
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Printo kontratën" arrow>
            <span>
              <Button size="small" variant="outlined"
                startIcon={<PrintIcon sx={{fontSize:15}}/>}
                onClick={isWordTemplate ? () => downloadWord('pdf', { open: true }) : printContract}
                disabled={!contractTemplate}
                sx={{borderRadius:1.5, fontWeight:700, textTransform:'none', height:34, px:1.8, flexShrink:0,
                  borderColor:'#e2e8f0', color:'text.secondary',
                  '&:hover':{borderColor:'#3E8FA3', color:'#3E8FA3'},
                  '&.Mui-disabled':{opacity:0.4}}}>
                Printo
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={contractTemplate ? t('worker.downloadContract') : 'Zgjidhni një template fillimisht'} arrow>
            <span>
              <Button size="small" variant="outlined"
                startIcon={docBusy ? <CircularProgress size={13}/> : <FileDownloadIcon sx={{fontSize:15}}/>}
                onClick={isWordTemplate ? (e) => setDownloadAnchor(e.currentTarget) : ()=>wordDoc('toWordDoc', worker?.FirstName + '_' + worker?.LastName)}
                disabled={!contractTemplate}
                sx={{borderRadius:1.5, fontWeight:700, textTransform:'none', height:34, px:1.8, flexShrink:0,
                  borderColor:'#e2e8f0', color:'text.secondary',
                  '&:hover':{borderColor:'#3E8FA3', color:'#3E8FA3'},
                  '&.Mui-disabled':{opacity:0.4}}}>
                Shkarko
              </Button>
            </span>
          </Tooltip>
          <Menu anchorEl={downloadAnchor} open={Boolean(downloadAnchor)} onClose={() => setDownloadAnchor(null)}>
            <MenuItem onClick={() => downloadWord('docx')}>Shkarko si Word (.docx)</MenuItem>
            <MenuItem onClick={() => downloadWord('pdf')}>Shkarko si PDF</MenuItem>
          </Menu>

          <Button size="small" variant="contained"
            startIcon={<SaveIcon sx={{fontSize:15}}/>}
            onClick={handleSubmit(onSubmit)}
            sx={{borderRadius:1.5, fontWeight:700, textTransform:'none', height:34, px:2, flexShrink:0,
              boxShadow:'none', background:'linear-gradient(135deg,#3E8FA3,#1B6A7A)',
              '&:hover':{boxShadow:'none', filter:'brightness(0.9)'}}}>
            Ruaj ndryshimet
          </Button>
        </Stack>

        {/* ── Worker info strip ── */}
        {!loader && worker?.FirstName && (
          <Box sx={t=>({
            display:'flex', alignItems:'center', gap:2, px:2.5, py:1.5, borderRadius:2,
            border:'1px solid', borderColor: alpha('#3E8FA3', 0.22),
            bgcolor: t.palette.mode==='dark' ? alpha('#3E8FA3',0.08) : '#f0f9fb',
          })}>
            <Avatar sx={{bgcolor:'#3E8FA3', width:44, height:44, fontSize:'1rem', fontWeight:700}}>
              {(worker.FirstName?.[0]??'')+(worker.LastName?.[0]??'')}
            </Avatar>
            <Box sx={{flex:1, minWidth:0}}>
              <Typography sx={{fontWeight:700, fontSize:'0.95rem', lineHeight:1.2}}>{worker.FirstName} {worker.LastName}</Typography>
              {worker.Profession && <Typography variant="caption" color="text.secondary">{worker.Profession}</Typography>}
            </Box>
            <Divider orientation="vertical" flexItem sx={{mx:0.5}}/>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <BadgeIcon sx={{color:'text.disabled', fontSize:16}}/>
              <Typography variant="caption" color="text.secondary" sx={{fontWeight:600}}>#{id}</Typography>
              {contractDetails?.StartDate && (
                <Chip size="small"
                  label={`${contractDetails.StartDate}${contractDetails.EndDate?' → '+contractDetails.EndDate:''}`}
                  sx={{fontSize:'0.7rem', height:21, bgcolor:'#EEF7FA', color:'#1B6A7A', fontWeight:600}}/>
              )}
              {contractDetails?.BaseSalary && (
                <Chip size="small" label={`€ ${contractDetails.BaseSalary}`}
                  sx={{fontSize:'0.7rem', height:21, bgcolor:alpha('#059669',0.1), color:'#059669', fontWeight:600}}/>
              )}
            </Stack>
          </Box>
        )}

        {loader && <LinearProgress sx={{borderRadius:1}}/>}

        {!loader && view === 'form' && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              {/* ── Section 1: Contract period ── */}
              <Box sx={t=>({
                borderRadius:2, border:'1px solid',
                borderColor: t.palette.mode==='dark'?'grey.800':'#e2e8f0',
                bgcolor: t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden',
              })}>
                <Stack direction="row" alignItems="center" spacing={1.2}
                  sx={{px:2.5, py:1.4, borderBottom:'1px solid', borderColor:'divider',
                    bgcolor: t=>t.palette.mode==='dark'?alpha('#3b82f6',0.07):alpha('#3b82f6',0.04)}}>
                  <Box sx={{width:28, height:28, borderRadius:1, bgcolor:alpha('#3b82f6',0.12),
                    display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <CalendarMonthIcon sx={{fontSize:15, color:'#3b82f6'}}/>
                  </Box>
                  <Typography sx={{fontWeight:700, fontSize:'0.82rem'}}>{t('worker.contractPeriod')}</Typography>
                </Stack>
                <Box sx={{p:2.5}}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>
                      {/* Nr. i protokollit — caktohet vetë kur kontrata gjenerohet; mund të ndryshohet me dorë. */}
                      <InputController placeholder="Nr. i protokollit" control={control} rules={{}}
                        register={{...register('ProtocolNumber')}}
                        symbol={!watch('ProtocolNumber') && (
                          <Button size="small" onClick={assignProtocol} disabled={protocolBusy}
                            sx={{textTransform:'none', fontWeight:700, minWidth:0, px:1, mr:-1}}>
                            {protocolBusy ? '…' : 'Cakto'}
                          </Button>
                        )}/>
                    </Grid>
                    <Grid item xs={12} sm={8} sx={{display:{xs:'none', sm:'block'}}}>
                      <Typography variant="caption" color="text.secondary" sx={{display:'block', pt:1.25}}>
                        Numri del në kontratë me fushën «Nr. i protokollit» ({'{!contract_number!}'} në Word). Pa numër, caktohet vetë herën e parë që gjenerohet — sipas formatit te Konfigurimi → Shabllonet → Kontratat → «Numërimi i protokollit».
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputController placeholder={t('worker.startDate')} error={errors.StartDate} control={control} type="date" register={{...register('StartDate')}}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputController placeholder={t('worker.endDate')} error={errors.EndDate} control={control} type="date" register={{...register('EndDate')}}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <SelectController placeholder={t('worker.contractExtension')} error={errors.ContractExtension} control={control} options={contractExtension} name="ContractType" register={{...register('ContractType')}}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <SelectController placeholder={t('worker.contractTemplate')} error={errors.Template} control={control} options={templates} name="Template" register={{...register('Template')}}/>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {/* ── Section 2: Organisation ── */}
              <Box sx={t=>({
                borderRadius:2, border:'1px solid',
                borderColor: t.palette.mode==='dark'?'grey.800':'#e2e8f0',
                bgcolor: t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden',
              })}>
                <Stack direction="row" alignItems="center" spacing={1.2}
                  sx={{px:2.5, py:1.4, borderBottom:'1px solid', borderColor:'divider',
                    bgcolor: t=>t.palette.mode==='dark'?alpha('#7c3aed',0.07):alpha('#7c3aed',0.04)}}>
                  <Box sx={{width:28, height:28, borderRadius:1, bgcolor:alpha('#7c3aed',0.12),
                    display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <BusinessIcon sx={{fontSize:15, color:'#7c3aed'}}/>
                  </Box>
                  <Typography sx={{fontWeight:700, fontSize:'0.82rem'}}>{t('worker.orgPlacement')}</Typography>
                </Stack>
                <Box sx={{p:2.5}}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>
                      <SelectController placeholder={t('worker.branch')} error={errors.BranchId} control={control} options={branch} name="BranchId" register={{...register('BranchId')}}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      {/* Vetëm departamentet që ekzistojnë në filialin e zgjedhur. */}
                      <SelectController placeholder={t('common.department')} error={errors.Department} control={control} options={departmentsForBranch(departments, watch('BranchId')?.Id ?? watch('BranchId')?.id)} name="Department" register={{...register('Department')}}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <SelectController placeholder={t('worker.workType')} error={errors.WorkType} control={control} options={workTypes} name="WorkType" register={{...register('WorkType')}}/>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              {/* ── Section 3: Salary ── */}
              <Box sx={t=>({
                borderRadius:2, border:'1px solid',
                borderColor: t.palette.mode==='dark'?'grey.800':'#e2e8f0',
                bgcolor: t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden',
              })}>
                <Stack direction="row" alignItems="center" spacing={1.2}
                  sx={{px:2.5, py:1.4, borderBottom:'1px solid', borderColor:'divider',
                    bgcolor: t=>t.palette.mode==='dark'?alpha('#059669',0.07):alpha('#059669',0.04)}}>
                  <Box sx={{width:28, height:28, borderRadius:1, bgcolor:alpha('#059669',0.12),
                    display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <AttachMoneyIcon sx={{fontSize:15, color:'#059669'}}/>
                  </Box>
                  <Typography sx={{fontWeight:700, fontSize:'0.82rem'}}>{t('worker.salaryAndBonuses')}</Typography>
                </Stack>
                <Box sx={{p:2.5}}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={3}>
                      <SelectController placeholder={t('worker.salaryType')} error={errors.SalaryType} control={control} options={salaryTypes} name="SalaryType" register={{...register('SalaryType')}}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <SelectController placeholder={t('worker.brutoNeto')} error={errors.BrutoNeto} control={control} options={brutoNeto} name="BrutoNeto" register={{...register('BrutoNeto')}}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputController placeholder={t('worker.baseSalary')} error={errors.BaseSalary} control={control} symbol="€" register={{...register('BaseSalary')}}
                        onFieldChange={(e) => applyPayChange(setValue, 'BaseSalary', e?.target?.value, payRates)}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputController placeholder={t('worker.hourSalary')} error={errors.HourSalary} control={control} symbol="€" register={{...register('HourSalary')}}
                        onFieldChange={(e) => applyPayChange(setValue, 'HourSalary', e?.target?.value, payRates)}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputController placeholder={t('worker.hourSalaryWeekend')} error={errors.HourSalaryWeekend} control={control} symbol="€" register={{...register('HourSalaryWeekend')}}/>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputController placeholder={t('worker.hourSalaryHoliday')} error={errors.HourSalaryHoliday} control={control} symbol="€" register={{...register('HourSalaryHoliday')}}/>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    Plotësoni pagën bazë <b>ose</b> pagën për orë — tjetra llogaritet vetë: ora = paga ÷ 173,33 orë (LP Neni 20).
                    Fundjava +{payRates.WeekendRate}% dhe festa +{payRates.HolidayRate}% sipas politikës fiskale; mund t'i ndryshoni me dorë.
                  </Typography>
                  {payRates.MinimumWage > 0 && Number(watch('BaseSalary')) > 0 && Number(watch('BaseSalary')) < payRates.MinimumWage && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      Paga bazë është nën pagën minimale ({payRates.MinimumWage} €).
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* ── Section 4: Contributions ── */}
              <Box sx={t=>({
                borderRadius:2, border:'1px solid',
                borderColor: watch('WithTax') ? alpha('#d97706',0.4) : (t.palette.mode==='dark'?'grey.800':'#e2e8f0'),
                bgcolor: t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden', transition:'border-color 0.2s',
              })}>
                <Stack direction="row" alignItems="center" spacing={1.2}
                  sx={{px:2.5, py:1.4, borderBottom: watch('WithTax')?'1px solid':'none', borderColor:alpha('#d97706',0.15),
                    bgcolor: t=>t.palette.mode==='dark'?alpha('#d97706',0.07):(watch('WithTax')?alpha('#d97706',0.06):'transparent')}}>
                  <Box sx={{width:28, height:28, borderRadius:1, bgcolor:alpha('#d97706',0.12),
                    display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <AccountBalanceIcon sx={{fontSize:15, color:'#d97706'}}/>
                  </Box>
                  <Typography sx={{fontWeight:700, fontSize:'0.82rem', flex:1}}>{t('worker.contributions')}</Typography>
                  <FormControlLabel sx={{mr:0}} control={
                    <Switch size="small"
                      onChange={e=>setTaxes(e.target.checked,"WithTax")}
                      checked={!!watch("WithTax")}
                      sx={{'& .MuiSwitch-switchBase.Mui-checked':{color:'#d97706'},
                           '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':{bgcolor:'#d97706'}}}/>
                  } label={<Typography variant="caption" sx={{fontWeight:600, color:watch('WithTax')?'#d97706':'text.secondary'}}>
                    {watch('WithTax') ? t('common.active') : t('common.inactive')}
                  </Typography>}/>
                </Stack>
                {watch('WithTax') && (
                  <Box sx={{p:2.5}}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={3}>
                        <InputController placeholder={t('worker.workerContribution')} error={errors.EmployeeTax} control={control} symbol="%" value={watch('EmployeeTax')??5} register={{...register('EmployeeTax')}} type="number"/>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <InputController placeholder={t('worker.employerContribution')} error={errors.EmployerTax} control={control} symbol="%" value={watch('EmployerTax')??5} register={{...register('EmployerTax')}} type="number"/>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>

              {/* ── Section 5: Contract termination ── */}
              <Box sx={t=>({
                borderRadius:2, border:'1px solid',
                borderColor: watch('DestroyContract') ? alpha('#ef4444',0.4) : (t.palette.mode==='dark'?'grey.800':'#e2e8f0'),
                bgcolor: t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden', transition:'border-color 0.2s',
              })}>
                <Stack direction="row" alignItems="center" spacing={1.2}
                  sx={{px:2.5, py:1.4, borderBottom: watch('DestroyContract')?'1px solid':'none', borderColor:alpha('#ef4444',0.15),
                    bgcolor: t=>t.palette.mode==='dark'?alpha('#ef4444',0.07):(watch('DestroyContract')?alpha('#ef4444',0.04):'transparent')}}>
                  <Box sx={{width:28, height:28, borderRadius:1, bgcolor:alpha('#ef4444',0.1),
                    display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <HighlightOffIcon sx={{fontSize:15, color:'#ef4444'}}/>
                  </Box>
                  <Typography sx={{fontWeight:700, fontSize:'0.82rem', flex:1}}>{t('worker.contractTermination')}</Typography>
                  <FormControlLabel sx={{mr:0}} control={
                    <Switch size="small"
                      onChange={e=>setTaxes(e.target.checked,"DestroyContract")}
                      checked={!!watch("DestroyContract")}
                      sx={{'& .MuiSwitch-switchBase.Mui-checked':{color:'#ef4444'},
                           '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':{bgcolor:'#ef4444'}}}/>
                  } label={<Typography variant="caption" sx={{fontWeight:600, color:watch('DestroyContract')?'#ef4444':'text.secondary'}}>
                    {watch('DestroyContract') ? t('common.active') : t('common.inactive')}
                  </Typography>}/>
                </Stack>
                {watch('DestroyContract') && (
                  <Box sx={{p:2.5}}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={3}>
                        <InputController placeholder={t('worker.terminationDate')} error={errors.DestroyDate} control={control} type="date" register={{...register('DestroyDate')}}/>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <InputController placeholder={t('worker.terminationReason')} error={errors.DestroyReason} control={control} value={watch('DestroyReason')??''} register={{...register('DestroyReason')}} type="text"/>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>

              {/* ── Section 6: Terms ── */}
              <Box sx={t=>({
                borderRadius:2, border:'1px solid',
                borderColor: t.palette.mode==='dark'?'grey.800':'#e2e8f0',
                bgcolor: t.palette.mode==='dark'?'#111827':'#fff', overflow:'hidden',
              })}>
                <Stack direction="row" alignItems="center" spacing={1.2}
                  sx={{px:2.5, py:1.4, borderBottom:'1px solid', borderColor:'divider',
                    bgcolor: t=>t.palette.mode==='dark'?alpha('#6366f1',0.07):alpha('#6366f1',0.04)}}>
                  <Box sx={{width:28, height:28, borderRadius:1, bgcolor:alpha('#6366f1',0.12),
                    display:'flex', alignItems:'center', justifyContent:'center'}}>
                    <DescriptionOutlinedIcon sx={{fontSize:15, color:'#6366f1'}}/>
                  </Box>
                  <Typography sx={{fontWeight:700, fontSize:'0.82rem'}}>{t('worker.contractConditions')}</Typography>
                </Stack>
                <Box sx={{p:2.5}}>
                  <TextField
                    multiline
                    minRows={5}
                    fullWidth
                    size="small"
                    label={t('worker.contractConditions')}
                    placeholder={t('worker.contractConditions') + '...'}
                    value={watch('TermsOfContract') ?? ''}
                    {...register('TermsOfContract')}
                    onChange={e => setValue('TermsOfContract', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{'& .MuiInputBase-input':{ fontFamily:'inherit', fontSize:'0.85rem', lineHeight:1.65 }}}
                  />
                </Box>
              </Box>

            </Stack>
          </form>
        )}

        {/* ── Contract Preview Panel ── */}
        {!loader && view === 'preview' && (
          <Box sx={t=>({
            borderRadius:2, border:'1px solid',
            borderColor: t.palette.mode==='dark'?'grey.800':'#e2e8f0',
            bgcolor: t.palette.mode==='dark'?'#111827':'#fff',
            overflow:'hidden',
          })}>
            {/* Preview header */}
            <Stack direction="row" alignItems="center" spacing={1.2}
              sx={{px:2.5, py:1.4, borderBottom:'1px solid', borderColor:'divider',
                bgcolor: t=>t.palette.mode==='dark'?alpha('#3E8FA3',0.07):alpha('#3E8FA3',0.04)}}>
              <Box sx={{width:28, height:28, borderRadius:1, bgcolor:alpha('#3E8FA3',0.12),
                display:'flex', alignItems:'center', justifyContent:'center'}}>
                <ArticleIcon sx={{fontSize:15, color:'#3E8FA3'}}/>
              </Box>
              <Typography sx={{fontWeight:700, fontSize:'0.82rem', flex:1}}>Preview i Kontratës</Typography>
              {templateLoading && <CircularProgress size={16} sx={{color:'#3E8FA3'}}/>}
              {!templateLoading && contractTemplate && (
                <Chip size="small" label={contractTemplate.Name || contractTemplate.name || 'Template'}
                  sx={{fontSize:'0.7rem', height:21, bgcolor:alpha('#3E8FA3',0.12), color:'#1B6A7A', fontWeight:600}}/>
              )}
              {!templateLoading && !contractTemplate && (
                <Chip size="small" label="Nuk ka template"
                  sx={{fontSize:'0.7rem', height:21, bgcolor:alpha('#ef4444',0.1), color:'#ef4444', fontWeight:600}}/>
              )}
            </Stack>

            <Box sx={{p:3}}>
              {templateLoading && (
                <Stack alignItems="center" justifyContent="center" py={6} spacing={1.5}>
                  <CircularProgress size={32} sx={{color:'#3E8FA3'}}/>
                  <Typography variant="caption" color="text.secondary">Duke ngarkuar template-in...</Typography>
                </Stack>
              )}
              {!templateLoading && !contractTemplate && (
                <Stack alignItems="center" justifyContent="center" py={6} spacing={1.5}>
                  <ArticleIcon sx={{fontSize:48, color:'text.disabled', opacity:0.4}}/>
                  <Typography color="text.secondary" sx={{fontWeight:600}}>Nuk ka template të zgjedhur</Typography>
                  <Typography variant="caption" color="text.disabled" textAlign="center">
                    Zgjidhni një template kontrate në seksionin "Periudha e kontratës" <br/>dhe ky seksion do të shfaqë kontratën e plotësuar.
                  </Typography>
                  <Button size="small" variant="outlined"
                    onClick={() => setView('form')}
                    sx={{borderRadius:1.5, textTransform:'none', fontWeight:700, borderColor:'#e2e8f0', color:'text.secondary',
                      '&:hover':{borderColor:'#3E8FA3', color:'#3E8FA3'}, mt:1}}>
                    Kthehu te forma
                  </Button>
                </Stack>
              )}
              {!templateLoading && contractTemplate && isWordTemplate && (
                filledDocx
                  ? <WordPreview file={filledDocx} maxHeight="none" />
                  : <Stack alignItems="center" py={6}><CircularProgress size={28} sx={{color:'#3E8FA3'}}/></Stack>
              )}
              {!templateLoading && contractTemplate && !isWordTemplate && (
                <Box
                  sx={{
                    fontFamily:'"Times New Roman", Times, serif',
                    fontSize:'12pt',
                    lineHeight:1.7,
                    color:'#111',
                    '& h1,& h2,& h3':{ marginBottom:'0.4em' },
                    '& p':{ marginBottom:'0.5em' },
                    '& table':{ borderCollapse:'collapse', width:'100%', marginBottom:'1em' },
                    '& td,& th':{ border:'1px solid #ddd', padding:'6px 10px', textAlign:'left' },
                  }}
                  dangerouslySetInnerHTML={{ __html: replaceText() }}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
